import React, { useRef } from "react"
import { useLocation } from "react-router-dom"

import { MainRefContext } from "shared/context/MainRefContext"
import { useAppSidebar } from "shared/lib/app-sidebar/app-sidebar-store"
import { useCurrentScrivitoPage } from "shared/lib/scrivito/scrivito-page-store"
import { useInertElement } from "shared/lib/useInertElement"
import { useOverlays } from "shared/lib/useOverlays"

import { Footer } from "app/ui/footer/Footer"

import { useHistoryActionListener } from "features/scroll-management/model/useHistoryActionListener"
import { useWindowScroll } from "features/scroll-management/model/useWindowScroll"
import { AppSidebarConnector } from "widgets/app-sidebar/AppSidebar"
import { ErrorBoundary } from "widgets/error-boundary/ErrorBoundary"
import { FeedbackButtonConnector } from "widgets/feedback-button/FeedbackButtonConnector"
import { HeaderConnector } from "widgets/header/HeaderConnector"
import { PageLoaderFactory } from "widgets/page-loader/PageLoaderFactory"

export const BaseLayout = React.memo(
  ({ children }: { children: React.JSX.Element }) => {
    const { key } = useLocation()
    const { currentPageObjID } = useCurrentScrivitoPage()
    const { currentOverlay } = useOverlays()

    const mainRef = useRef(null)
    const footerRef = useRef<HTMLDivElement>(null)
    const contentRef = useRef<HTMLDivElement>(null)

    const { isOpen: isAppSidebarOpen } = useAppSidebar()

    useInertElement({
      element: contentRef.current,
      condition: !!currentOverlay
    })

    useInertElement({
      element: mainRef.current,
      condition: !!currentOverlay || isAppSidebarOpen
    })

    useInertElement({
      element: footerRef.current,
      condition: !!currentOverlay || isAppSidebarOpen
    })

    useWindowScroll()
    useHistoryActionListener()

    return (
      <ErrorBoundary>
        <MainRefContext.Provider value={mainRef}>
          <div id="app" className="app">
            <div id="app-content" className="app-content" ref={contentRef}>
              <HeaderConnector />
              <main id="main" className="main" ref={mainRef}>
                <ErrorBoundary locationKey={`${key}-${currentPageObjID}`}>
                  {children}
                </ErrorBoundary>
                <div className="page-loader">
                  <PageLoaderFactory />
                </div>
              </main>
              <AppSidebarConnector />
              <FeedbackButtonConnector />
              <Footer footerRef={footerRef} />
            </div>
          </div>
        </MainRefContext.Provider>
      </ErrorBoundary>
    )
  }
)
